<!--
  AB#22511 — the Consolidation line in the order header: the group this item
  ships in, how many orders it holds, the group's AWB (or "not booked"), and
  one chip per other order with its status. The group itself is managed in
  Shipping, the one group screen; "Open in Shipping" goes there. The AWB is
  shown as given: the carrier is not assumed. Nothing is drawn when the item
  is in no group.
-->
<template>
  <div v-if="block && block.group_key" class="ohd-ref cl" data-test="ohd-consolidation">
    <span class="ohd-ref-lbl">Consolidation</span>
    <span class="cl-val">
      <b data-test="cl-group">{{ block.group_key }}</b>
      <span class="cl-dot">·</span>
      <span>{{ count }} order{{ count === 1 ? '' : 's' }}</span>
      <span class="cl-dot">·</span>
      <span v-if="block.awb_number" class="cl-mono" data-test="cl-awb"
        >AWB {{ block.awb_number }}</span
      >
      <span v-else class="cl-mute" data-test="cl-awb">not booked</span>
      <span
        v-for="s in block.siblings"
        :key="s.shipping_order_id"
        class="cl-chip"
        :data-test="`cl-sibling-${s.reference}`"
        >{{ s.reference
        }}<template v-if="statusLabel(s.status)">
          · <i>{{ statusLabel(s.status) }}</i></template
        ></span
      >
      <router-link
        v-if="searchTerm"
        :to="{ path: '/shipping/queue', query: { search: searchTerm } }"
        class="cl-link"
        data-test="cl-open-shipping"
        >Open in Shipping</router-link
      >
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { CustimooOrderFlowStatuses } from '@/composables';
import type { OrderDetailItem } from '@/models/OrderDetailItem';

const props = defineProps<{ item: OrderDetailItem }>();

const block = computed(() => props.item.consolidation ?? null);
/** This order plus the others in its group. */
const count = computed(() => (block.value?.siblings.length ?? 0) + 1);
/** The Shipping queue searches by order number; the item's own number finds its row (no number, no link). */
const searchTerm = computed(() => String(props.item.item_no ?? '').trim());
const statusLabel = (status: string | null): string | null =>
  status ? ((CustimooOrderFlowStatuses[status] as string | undefined) ?? status) : null;
</script>

<style scoped>
.cl {
  align-items: baseline;
}
.cl-val {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 5px;
  min-width: 0;
}
.cl-dot {
  color: var(--ink-mute);
}
.cl-mute {
  color: var(--ink-mute);
}
.cl-mono {
  font-variant-numeric: tabular-nums;
}
.cl-chip {
  font-size: 11px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--surface-3);
  color: var(--ink-soft);
}
.cl-chip i {
  font-style: normal;
  font-weight: 500;
}
.cl-link {
  font-size: 12.5px;
  color: var(--accent);
  text-decoration: underline dotted;
}
.cl-link:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: 3px;
}
@media (max-width: 767.98px) {
  .cl-link {
    min-height: 44px;
    display: inline-flex;
    align-items: center;
  }
}
</style>
